import type { Category, ProductProjection } from '~~/types/commercetools';
import type { ProductVariantExtended } from '~~/types/product/ProductVariantExtended';
import { getVatPercentage } from '~~/helpers/product/getVatPercentage';
import type { CountryCodeValue } from '~~/types/localization/localizationHelperTypes';
import type { ProductVariantGql } from '~~/types/product/ProductVariantGql';
import { CATEGORY_TREE } from '~~/constants/categoryTree';
import { PRODUCT_ATTRIBUTES } from '~~/constants/products';
import { getTranslatedAttributeValue } from '~~/helpers/product/getTranslatedAttributeValue';

interface QueryParams {
  isPublished?: boolean;
  inStock?: boolean;
}

const getEligibleForSellingInCurrentMarket = (
  variant: ProductVariantExtended, commerceToolsLocale: string): boolean => {

  const eligibleForSellingAttr = variant.attributesRaw?.find(
    attr => attr.name === PRODUCT_ATTRIBUTES.ELIGIBLE_FOR_SELLING);

  const value = eligibleForSellingAttr
    ? getTranslatedAttributeValue(eligibleForSellingAttr, commerceToolsLocale)
    : undefined;

  return value === true || value === 'true';

};

const extendProducts = (
  products: readonly (ProductVariantGql | ProductProjection)[],
  commerceToolsLocale: string,
  countryCode: CountryCodeValue,
  params: QueryParams = {},
): ProductVariantExtended[] => {

  return products
    ?.filter((product: any) => {

      if ('masterData' in product) {

        return params.isPublished ? product.masterData?.published : true;

      } else {

        return params.isPublished ? product.published : true;

      }

    })
    .filter((product: any) => {

      if ('masterData' in product) {

        return params.inStock
          ? product.masterData?.current?.allVariants?.[0]?.availability?.noChannel?.isOnStock
          : true;

      } else {

        return params.inStock
          ? product.allVariants?.[0]?.availability?.noChannel?.isOnStock
          : true;

      }

    })
    .map((product: any) => {

      const current = 'masterData' in product ? product.masterData.current : product;
      const categories: Category[] = current.categories;

      return current.allVariants.map((variant) => ({
        ...variant,
        attributesRaw: variant.attributesRaw?.map((raw) => ({
          ...raw,
          _translated: getTranslatedAttributeValue(raw, commerceToolsLocale),
        })) || [],
        _name: current.name,
        _slug: current.slug,
        _id: product.id,
        _key: product.key,
        _master: current.masterVariant.id === variant.id,
        _description: current.description,
        _meta: {
          description: current.metaDescription,
          keywords: current.metaKeywords,
          title: current.metaTitle,
        },
        _categoriesRef: current.categoriesRef?.map((cr) => cr.id),
        _breadcrumbs: categories
          ?.filter(category => category.ancestors?.[0]?.key === CATEGORY_TREE.DEFAULT.key)
          ?.map((c) => {

            let res = [];

            try {

              for (const ancestor of c.ancestors) {

                res.push({
                  name: ancestor.name,
                  slug: ancestor.slug,
                  id: ancestor.id,
                });

              }

              res.push({
                name: c.name,
                slug: c.slug,
                id: c.id,
              });

            } catch (e) {

              res = [];

            }

            return res;

          }),
        _rating: (product as any).reviewRatingStatistics,
        _slugAllLocales: current.slugAllLocales,
        _categories: current.categories,
        vatPercentage: getVatPercentage(product, countryCode),
        _eligibleForSellingInCurrentMarket: getEligibleForSellingInCurrentMarket(variant, commerceToolsLocale),
        _published: product.masterData?.published,
      }));

    })
    .reduce((prev, curr) => [...prev, ...curr], []);

};

export default extendProducts;
